import { Fragment, memo, SyntheticEvent, useEffect, useRef, useState } from "react";
import { Table as BootstrapTable, Button } from "react-bootstrap";
import { BsTrash } from "react-icons/bs";
import { BsSave } from "react-icons/bs";
import { FaCheck, FaSearch } from "react-icons/fa";
import { FaX } from "react-icons/fa6";
import { LiaPlusSolid } from "react-icons/lia";
import { RiArrowDownSLine, RiArrowUpSLine } from "react-icons/ri";

import TextInput from "../../../shared/components/text-input/text-input";
import Tooltip from "../../../shared/components/tooltip/tooltip";
import { IAction, IDataType } from "../../../shared/definitions/datatypes";
import Checkbox from "../checkbox/checkbox";
import "./controlled-table.scss";


export interface IAnyString {
  [index: string]: string;
}

export interface ITableRowData {
  id: string;
  [index: string]: any; // eslint-disable-line
}

export type MarkVariant = "highlighted" | "faded";

export interface IMarkedRow {
  id: string;
  mark?: MarkVariant;
}

export interface IColumn {
  key: string;
  displayName: string;
  dataType: IDataType;
  sortable?: boolean;
}

export interface ITableConfig {
  title?: string;
  columns: IColumn[];
  data: ITableRowData[];
  preSelectedIds?: string[];
  style?: "default" | "compact";
  readOnly?: boolean;
  decimalFormatting?: boolean;
  customSaveButtonText?: string;
  allowCollapsedText?: boolean;
  textTruncateLimit?: number;
  selectionMode: "multi" | "single" | "off";
  externalSearch?: boolean;
  highlightOnSearch?: boolean;
  searchAll?: boolean;
  pageSize?: number;
  markedRows?: IMarkedRow[];
  extraButtons?: JSX.Element;
  allowSelectAll?: boolean;
  customSaveButtonTooltip?: string;
  showFalse?: boolean;
  rowHeight?: "small" | "medium" | "large";
  disableSaveButton?: boolean;
  onDeleted?: (selectedIds: string[]) => void;
  onSorted: (sortedByCol: ISortedBy) => void;
  onNewClicked?: () => void;
  onRowClick: (id: string) => void;
  onScroll: () => void;
  onSelectionChange?: (selectedIds: string[]) => void;
  onSaveClicked?: () => void;
  onActionClick?: (action_key: string, rowId: string) => void;
  externalSearchValue?: (value: string) => void;
}

interface ISortedBy {
  column: undefined | string;
  direction: undefined | string;
}

const ControlledTable = ({
  title,
  columns,
  data,
  preSelectedIds,
  selectionMode = "multi",
  externalSearch,
  style = "default",
  externalSearchValue,
  highlightOnSearch,
  onDeleted,
  onSorted,
  onNewClicked,
  onSaveClicked,
  onScroll,
  onRowClick,
  onSelectionChange,
  onActionClick,
  readOnly,
  decimalFormatting,
  allowCollapsedText,
  markedRows = [],
  textTruncateLimit = 100,
  customSaveButtonText,
  customSaveButtonTooltip,
  extraButtons,
  allowSelectAll,
  rowHeight,
  showFalse,
  disableSaveButton
}: ITableConfig) => {
  const [checkAll, setCheckAll] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const [selectedRows, setSelectedRows] = useState<string[]>(preSelectedIds || []);
  const [scrolled, setScrolled] = useState(false);
  const [sortedByCol, setSortedByCol] = useState<ISortedBy>({ column: undefined, direction: undefined });
  const [expandedRow, setExpandedRow] = useState<string>("");

  const getMarkClass = (id: string): string | null => {
    const entry = markedRows.find((r) => r.id === id);
    if (!entry) return null;
    const variant: MarkVariant = entry.mark ?? "highlighted";
    return `controlledTable-row--marked-${variant}`;
  };

  useEffect(() => {
    setSelectedRows(preSelectedIds || []);
  }, [preSelectedIds]);

  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectedRows);
    }
  }, [selectedRows]);

  const isInternalUpdate = useRef(false);
  const node = useRef<any>(); // eslint-disable-line

  const highlightText = (text: string, searchTerm: string): JSX.Element => {
    if (!searchTerm.trim()) {
      return <span>{text}</span>;
    }

    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);

    return (
      <span>
        {parts.map((part, index) =>
          regex.test(part) ? (
            <mark key={index} className="controlledTable-highlight">
              {part}
            </mark>
          ) : (
            part
          )
        )}
      </span>
    );
  };

  const CollapsibleText = ({ text, limit, searchTerm, highlightSearch, isExpanded, handleExpandCollapse }: {
    text: string;
    limit: number;
    searchTerm?: string;
    highlightSearch?: boolean;
    isExpanded: boolean;
    handleExpandCollapse: () => void;
  }) => {
    if (!text || text.length <= limit) {
      const content = highlightSearch && searchTerm ? highlightText(text || '', searchTerm) : text;
      return <div>{content}</div>;
    }

    const truncatedText = text.substring(0, limit);
    const displayText = isExpanded ? text : `${truncatedText}...`;
    const content = highlightSearch && searchTerm ? highlightText(displayText, searchTerm) : displayText;

    return (
      <div className="controlledTable-collapsibleText">
        {content}
        <button
          className="controlledTable-collapsibleText-toggle"
          onClick={(e) => {
            e.stopPropagation();
            handleExpandCollapse();
          }}
          type="button"
        >
          {isExpanded ? " Show less" : " Show more"}
        </button>
      </div>
    );
  };

  const handleOnSelectRow = (id: string) => {
    if (readOnly) {
      return;
    }
    isInternalUpdate.current = true;

    const result = [...selectedRows];

    if (selectionMode === "multi") {
      if (result.indexOf(id) === -1) {
        result.push(id);
        setSelectedRows(result);
      } else {
        const filteredListOfIds = result.filter((el) => el !== id);
        setSelectedRows(filteredListOfIds);
      }
    } else {
      if (result.indexOf(id) === -1) {
        setSelectedRows([id]);
      } else {
        setSelectedRows([]);
      }
    }
  };


  const handleOnSelectAllRows = () => {
    if (selectionMode === "multi") {
      isInternalUpdate.current = true;

      const filteredData = getFilteredData();

      if (!checkAll) {
        const result = [...selectedRows, ...filteredData.map((row) => row.id).filter(id => !selectedRows.includes(id))];
        setSelectedRows(result);
        setCheckAll(true);
      } else {
        const visibleIds = filteredData.map(row => row.id);
        const result = selectedRows.filter(id => !visibleIds.includes(id));
        setSelectedRows(result);
        setCheckAll(false);
      }
    }
  };

  const isSelected = (id: string) => {
    return selectedRows.indexOf(id) !== -1;
  };

  const handleOnRowClick = (evt: React.MouseEvent, rowId: string) => {
    if (readOnly) {
      return;
    }
    evt.stopPropagation();
    onRowClick(rowId);
  };

  const shouldCollapseText = (columnData: any, column: IColumn): boolean => { // eslint-disable-line
    if (data.length === 0) {
      return false;
    }
    return allowCollapsedText === true &&
      typeof columnData === "string" &&
      columnData.length > textTruncateLimit &&
      column.dataType.type !== "action" &&
      column.dataType.type !== "dateTime";
  };

  const shouldHighlightText = (): boolean => {
    if (data.length === 0) {
      return false;
    }
    return highlightOnSearch === true &&
      externalSearch === true &&
      activeSearchTerm.trim() !== "";
  };

  const getFilteredData = () => {
    if (data.length === 0) {
      return [];
    }

    if (!searchInputValue) {
      return data;
    }

    return data.filter(row => {
      const columnsKeys = columns.map(column => column.key);
      let resultFilteredForMatching: IAnyString = {};
      let concatResultFilteredForMatching = "";

      columnsKeys.forEach(key => {
        resultFilteredForMatching = { ...resultFilteredForMatching, [key]: row[key] };
      });

      for (const key in resultFilteredForMatching) {
        concatResultFilteredForMatching = concatResultFilteredForMatching.toString().toLowerCase() + " " + (resultFilteredForMatching[key] ? resultFilteredForMatching[key].toString().toLowerCase() : "");
      }

      return concatResultFilteredForMatching.indexOf(searchInputValue.toLowerCase()) > -1;
    });
  };

  const handleExternalSearch = () => {
    if (data.length === 0) {
      return;
    }
    if (externalSearchValue) {
      setActiveSearchTerm(searchInputValue.trim());
      externalSearchValue(searchInputValue.trim());
    }
  };

  const handleClearSearch = () => {
    setSearchInputValue("");
    setActiveSearchTerm("");

    if (externalSearchValue) {
      externalSearchValue("");
    }
  };

  const handleSearchInputChange = (value: string) => {
    setSearchInputValue(value);

    if (!externalSearch) {
      setActiveSearchTerm(value.trim());
      return;
    }

    if (value.trim() === "") {
      setActiveSearchTerm("");
    }
  };

  const renderCellContent = (displayData: any, column: IColumn, rowId: string) => { // eslint-disable-line
    const shouldHighlight = shouldHighlightText();

    if (shouldCollapseText(displayData, column)) {
      return (
      <CollapsibleText
          text={displayData}
          limit={textTruncateLimit}
          searchTerm={shouldHighlight ? activeSearchTerm : undefined}
          highlightSearch={shouldHighlight}
          isExpanded={expandedRow === rowId}
          handleExpandCollapse={() => setExpandedRow(expandedRow === rowId ? "" : rowId)}
        />
      );
    } else if (shouldHighlight && typeof displayData === "string") {
      return highlightText(displayData, activeSearchTerm);
    } else {
      return displayData;
    }
  };

  const renderActionButton = (column: IColumn, rowData: ITableRowData) => {
    const actionDatatype = column.dataType as IAction;
    if (actionDatatype.disabled && actionDatatype.disabled(rowData)) {
      return <span>{actionDatatype.displayName?.(rowData) || actionDatatype.buttonText}</span>;
    }
    if (actionDatatype.buttonStyle !== "none")  
    {
      return <Button 
        onClick={(e) => {
          e.stopPropagation();
          onActionClick?.(column.key, rowData.id);
        }} 
        style={{backgroundColor: actionDatatype.buttonColor || "primary", borderColor: actionDatatype.borderColor || "primary"}}
        >
        {actionDatatype.displayName?.(rowData) || actionDatatype.buttonText}
      </Button>
    }
    if (actionDatatype.tooltipText) {
        return <Tooltip 
          content={actionDatatype.tooltipText || ""} 
          delay={300} 
          direction="bottom"
        >
          <p onClick={(e) => {
            e.stopPropagation();
            onActionClick?.(column.key, rowData.id);
          }} style={{backgroundColor: actionDatatype.buttonColor || "primary", borderColor: actionDatatype.borderColor || "primary"}}>{actionDatatype.displayName?.(rowData) || actionDatatype.buttonText}
          </p>
        </Tooltip>
    }
    return <p onClick={(e) => {
      e.stopPropagation();
      onActionClick?.(column.key, rowData.id);
    }} style={{backgroundColor: actionDatatype.buttonColor || "primary", borderColor: actionDatatype.borderColor || "primary"}}>{actionDatatype.displayName?.(rowData) || actionDatatype.buttonText}
    </p>
  };

  const renderRows = () => {
    if (data.length === 0) {
      return <tr className="controlledTable-row"><td colSpan={columns.length + (selectionMode !== "off" ? 1 : 0)}><span className="controlledTable-noData">No data to be displayed...</span></td></tr>;
    }
    const displayData = externalSearch ? data : getFilteredData();

    return displayData.map((rowData: ITableRowData, i: number) => {
      const rowMarkClass = getMarkClass(rowData.id);
      const rowClass = `controlledTable-row controlledTable-row-customRowHeight-${rowHeight}`;

      return (
        <tr
          className={rowClass}
          key={i}
          onClick={(e) => handleOnRowClick(e, rowData.id)}
        >
          {selectionMode !== "off" && <td key={rowData.id} className={rowMarkClass ?? ''}>
            <span>
              <Checkbox
                checked={isSelected(rowData.id)}
                onClick={() => handleOnSelectRow(rowData.id)}
              />
            </span>
          </td>
          }
          {columns.map((column) => {
            let columnData = rowData[column.key];
            if (column.dataType.type === "action") {
              let shouldShow = column.dataType.showCondition
                ? rowData.action_type === column.dataType.showCondition
                : (column.dataType.show !== false);
              if (column.dataType.shouldShow) {
                shouldShow = column.dataType.shouldShow(rowData);
              }

              if (shouldShow) {
                columnData = (
                  <div className="controlledTable-actionButtons">
                    {renderActionButton(column, rowData)}
                  </div>
                );
              } else {
                columnData = undefined;
              }
            }
            else if (columnData === true) {
              columnData = (
                <Fragment>
                  <FaCheck className="controlledTable-activeIcon" />
                </Fragment>
              );
            }
            else if (columnData === false) {
              if(showFalse){
                columnData = (
                  <Fragment>
                    <FaX className="controlledTable-activeIcon"/>
                  </Fragment>
                );
              }
              else{
                columnData = (
                  <Fragment></Fragment>
                );
              }
            }
            if (column.dataType.type === "dateTime" && columnData) {
              const parsedDate = new Date(columnData).toLocaleString();
              if (parsedDate === "Invalid Date") {
                columnData = rowData[column.key];
              } else {
                columnData = parsedDate;
              }
            }
            if (column.dataType.type === "reference" && columnData) {
              columnData = rowData[`${column.key}_name`];
            }
            if (column.dataType.type === "enumeration" && columnData) {
              columnData = column.dataType.values.find(val => val.id === columnData)?.name || columnData;
            }
            if (
              column.dataType.type === "number" &&
              columnData !== null &&
              columnData !== undefined &&
              column.dataType.decimalPlaces !== undefined
            ) {
              const parsedNumber = parseFloat(columnData);
              if (isNaN(parsedNumber)) {
                columnData = rowData[column.key];
              } else {
                columnData = parsedNumber.toFixed(column.dataType.decimalPlaces);
              }
            }
            let displayData = columnData;
            if (decimalFormatting && displayData != undefined && column.dataType.type === "number") {
              displayData = Number(displayData).toFixed(2);
            }


            return (
              <td key={column.key} className={rowMarkClass ?? ''}>
                <span>
                  {renderCellContent(displayData, column, rowData.id)}
                </span>
              </td>
            );
          })}
        </tr>
      );
    });
  };

  const sortColumVals = (key: string) => {
    let defineDirection;
    if (sortedByCol.column === key && sortedByCol.direction === "asc") {
      defineDirection = "desc";
    } else {
      defineDirection = "asc";
    }

    setSortedByCol({ column: key, direction: defineDirection });
    onSorted({ column: key, direction: defineDirection });
  };

  const defineSortingArrows = (key: string) => {
    if (data.length === 0) {
      return;
    }
    if (sortedByCol.column === key && sortedByCol.direction === "desc") {
      return <RiArrowUpSLine />;
    } else {
      return <RiArrowDownSLine />;
    }
  };

  const onTableScroll = (evt: SyntheticEvent<HTMLDivElement>) => {
    if (data.length === 0) {
      return;
    }
    const offset = Math.abs(evt.currentTarget.scrollHeight - evt.currentTarget.scrollTop - evt.currentTarget.clientHeight);
    const bottom = offset <= 50;

    if (bottom) {
      onScroll();
    }

    setScrolled(evt.currentTarget.scrollTop > 0);
  };

  useEffect(() => {
    if (data.length === 0 || externalSearch) {
      return;
    }

    const filteredData = getFilteredData();
    const visibleIds = filteredData.map(row => row.id);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedRows.includes(id));
    setCheckAll(allVisibleSelected);
  }, [selectedRows, searchInputValue, data, externalSearch]);

  return (
    <div className={`controlledTable controlledTable-${style}`}>
      <div className="controlledTable-buttonsGroup">
        <div className="controlledTable-header">
          {title && <h2>{title}</h2>}
        </div>
        <div className="controlledTable-actions">
          {externalSearch ? (
            <div className="controlledTable-searchGroup">
              <TextInput
                type={"text"}
                id={"controlledTable-searchInput"}
                required={false}
                inputKey={"controlledTable-searchInput"}
                autofocus={false}
                placeholder={"Type to filter"}
                controlled={true}
                value={searchInputValue}
                handleOnChange={handleSearchInputChange}
              />
              <Button
                onClick={handleExternalSearch}
                className="controlledTable-searchButton"
                variant="primary"
              >
                <FaSearch />
              </Button>
              {(searchInputValue || activeSearchTerm) && (
                <Button
                  onClick={handleClearSearch}
                  className="controlledTable-clearButton"
                  variant="secondary"
                  size="sm"
                >
                  Clear
                </Button>
              )}
            </div>
          ) : (
            <TextInput
              type={"text"}
              id={"controlledTable-searchInput"}
              required={false}
              inputKey={"controlledTable-searchInput"}
              autofocus={false}
              placeholder={"Type to filter"}
              controlled={true}
              value={searchInputValue}
              handleOnChange={handleSearchInputChange}
            />
          )}
          {extraButtons && extraButtons}
          {onDeleted && !readOnly &&
            <Tooltip content={"Delete"} direction="top" delay={350} >
              <Button name="delete"
                disabled={selectedRows.length === 0}
                onClick={() => {
                  onDeleted(selectedRows);
                  setSelectedRows([]);
                }}
                className="controlledTable-delete"
                variant="primary">
                <BsTrash />
              </Button>
            </Tooltip>}          
          {onNewClicked && !readOnly &&
            <Tooltip content={"Add New"} direction="top" delay={350} >
              <Button
                name="add"
                onClick={onNewClicked}
                className="controlledTable-new"
                variant="primary">
                <LiaPlusSolid />
              </Button>
            </Tooltip>}
          {onSaveClicked && !readOnly &&
            <Tooltip content={customSaveButtonTooltip || "Save"} direction="top" delay={350} >
              <Button
                name="save"
                onClick={onSaveClicked}
                className="controlledTable-save"
                variant="primary"
                disabled={disableSaveButton}
                >
                {customSaveButtonText || <BsSave />}
              </Button>
            </Tooltip>}
        </div>
      </div>
      <div ref={node} onScroll={onTableScroll} className={`controlledTable-container ${scrolled ? "scrolled" : ""}`}>
        <BootstrapTable size="sm" borderless striped>
          <thead>
            <tr>
              {
                selectionMode !== "off" ?
                  <th className="controlledTable-actionTh" key={"actions"}>
                    <span>
                      {selectionMode === "multi" && allowSelectAll !== false ? (
                        <Checkbox
                          checked={checkAll}
                          onClick={handleOnSelectAllRows}
                        />
                      ) : null}
                    </span>
                  </th>
                  : ""
              }
              {columns.map((column) => (
                <th
                  onClick={() => column.sortable !== false && sortColumVals(column.key)}
                  className={`${column.sortable !== false ? "controlledTable-sortable" : ""} ${column.key === sortedByCol.column ? "controlledTable-selectedCol" : ""}`}
                  key={column.key}>
                  <span>
                    {column.displayName} {column.sortable !== false && defineSortingArrows(column.key)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{renderRows()}</tbody>
        </BootstrapTable>
      </div>
    </div>
  );
};

export default memo(ControlledTable);